import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { CheckCircle, Plant, XCircle } from '@phosphor-icons/react'
import { useCan } from '@/auth/context'
import { Button, DataTable, EmptyState, type Column } from '@/ui'
import { errorMessage } from '../api/core'
import { useMarkReady, useReadiness, type ReadinessRow } from '../api/farmers'
import { useVillages } from '../api/reference'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { SelectFilter } from '../components/FilterBar'
import { ConsolePage } from '../components/Layout'
import { Code, Status } from '../components/bits'
import { useUrlState } from '../lib/useUrlState'

const STAGE_LABEL: Record<string, string> = {
  SOWING: 'Sowing',
  GERMINATION: 'Germination',
  VEGETATIVE: 'Vegetative',
  FLOWERING: 'Flowering',
  FRUITING: 'Fruiting',
  MATURATION: 'Maturation',
  HARVESTING: 'Ready for harvest',
}

/**
 * Ready for harvest (V1): which of a village's cultivations the gate may buy from. Farms belong to
 * the village through group_farms or their farmers (owner or farm users); marking one ready sets its
 * stage HARVESTING. The phone offers a farmer only his ready crops, and buys nothing without one.
 */
export function HarvestReadinessPage() {
  const can = useCan()
  const villages = useVillages()
  const [f, setF] = useUrlState({ groupId: '', cropId: '', ready: '' as '' | 'YES' | 'NO' })
  const q = useReadiness({ groupId: f.groupId || undefined, ready: f.ready })
  const mark = useMarkReady()
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [confirm, setConfirm] = useState<{ ids: string[]; ready: boolean } | null>(null)
  const canMark = can('farmers:update')

  const all = q.data?.rows ?? []
  const crops = useMemo(() => {
    const m = new Map<string, string>()
    for (const r of all) m.set(r.cropId, r.cropName)
    return [...m.entries()].map(([value, label]) => ({ value, label }))
  }, [all])
  const rows = f.cropId ? all.filter((r) => r.cropId === f.cropId) : all
  const pickedRows = rows.filter((r) => picked.has(r.cultivationId))
  const allPicked = rows.length > 0 && rows.every((r) => picked.has(r.cultivationId))
  const toggle = (id: string) =>
    setPicked((s) => {
      const n = new Set(s)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  const village = villages.data?.find((v) => v.id === f.groupId)

  const columns: Column<ReadinessRow>[] = [
    ...(canMark
      ? [
          {
            id: 'pick',
            header: (
              <input
                type="checkbox"
                aria-label="Select every cultivation shown"
                className="h-4 w-4 accent-amber"
                checked={allPicked}
                disabled={rows.length === 0}
                onChange={() =>
                  setPicked(allPicked ? new Set() : new Set(rows.map((r) => r.cultivationId)))
                }
              />
            ),
            cell: (r: ReadinessRow) => (
              <input
                type="checkbox"
                aria-label={`Select ${r.cropName} on ${r.farmCode ?? r.farmName ?? 'farm'}`}
                className="h-4 w-4 accent-amber"
                checked={picked.has(r.cultivationId)}
                onClick={(e) => e.stopPropagation()}
                onChange={() => toggle(r.cultivationId)}
              />
            ),
          },
        ]
      : []),
    {
      id: 'farmer',
      header: 'Farmer',
      cell: (r) =>
        r.farmers.length === 0 ? (
          <span className="text-[13px] text-warn">No farmer linked</span>
        ) : (
          <span className="flex flex-col gap-0.5">
            {r.farmers.map((x) => (
              <Link
                key={x.id}
                to={`/c/farmers/${x.id}`}
                className="hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                <Code>{x.farmerCode}</Code> <span className="text-[14px]">{x.name}</span>
              </Link>
            ))}
          </span>
        ),
    },
    {
      id: 'crop',
      header: 'Crop',
      cell: (r) => (
        <span className="flex flex-col">
          <span className="font-semibold">{r.cropName}</span>
          {r.varietyName && <span className="text-[12px] text-muted">{r.varietyName}</span>}
        </span>
      ),
    },
    {
      id: 'farm',
      header: 'Farm · plot',
      cell: (r) => (
        <span className="flex flex-col">
          <Code>{r.farmCode ?? r.farmName ?? '—'}</Code>
          <span className="text-[12px] text-muted">
            {r.plotLabel ?? ''}
            {r.area ? ` · ${Number(r.area).toFixed(2)} ${(r.areaUnit ?? '').toLowerCase()}` : ''}
          </span>
        </span>
      ),
      hideBelow: 'sm',
    },
    {
      id: 'sown',
      header: 'Sown',
      cell: (r) => <span className="font-mono text-[13px]">{r.startDate ?? '—'}</span>,
      hideBelow: 'md',
    },
    {
      id: 'stage',
      header: 'Stage',
      cell: (r) =>
        r.ready ? (
          <Status status="ACTIVE" label="Ready for harvest" />
        ) : (
          <span className="text-[14px]">{STAGE_LABEL[r.stage] ?? r.stage}</span>
        ),
    },
    ...(canMark
      ? [
          {
            id: 'act',
            header: '',
            align: 'right' as const,
            cell: (r: ReadinessRow) =>
              r.ready ? (
                <Button
                  size="sm"
                  variant="ghost"
                  leadingIcon={<XCircle />}
                  onClick={() => setConfirm({ ids: [r.cultivationId], ready: false })}
                >
                  Not ready
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  leadingIcon={<CheckCircle />}
                  onClick={() => setConfirm({ ids: [r.cultivationId], ready: true })}
                >
                  Mark ready
                </Button>
              ),
          },
        ]
      : []),
  ]

  return (
    <ConsolePage
      eyebrow="Farm gate"
      title="Ready for harvest"
      description="The gate buys a farmer's crop only from a cultivation marked ready here. Mark them a day or two before the collection day; phones get it with their next roster download."
    >
      {q.data && f.groupId && (
        <div className="grid grid-cols-2 gap-3 sm:max-w-md">
          <div className="rounded-card border border-rule bg-surface p-3">
            <div className="eyebrow">Ready for harvest</div>
            <div className="text-[24px] font-semibold">{q.data.ready}</div>
          </div>
          <div className="rounded-card border border-rule bg-surface p-3">
            <div className="eyebrow">Cultivations in {village?.name ?? 'the village'}</div>
            <div className="text-[24px] font-semibold">{q.data.total}</div>
          </div>
        </div>
      )}

      <DataTable<ReadinessRow>
        caption="Cultivations"
        columns={columns}
        rows={rows}
        getRowId={(r) => r.cultivationId}
        loading={Boolean(f.groupId) && q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        pageSize={100}
        filters={
          <>
            <SelectFilter
              label="Village"
              value={f.groupId}
              onChange={(v) => {
                setPicked(new Set())
                setF({ groupId: v, cropId: '' })
              }}
              options={(villages.data ?? []).map((v) => ({ value: v.id, label: v.name }))}
              allLabel="Pick a village"
            />
            <SelectFilter
              label="Crop"
              value={f.cropId}
              onChange={(v) => setF({ cropId: v })}
              options={crops}
              allLabel="All crops"
            />
            <SelectFilter<'YES' | 'NO'>
              label="Show"
              value={f.ready}
              onChange={(v) => setF({ ready: v })}
              options={[
                { value: 'YES', label: 'Ready for harvest' },
                { value: 'NO', label: 'Not ready yet' },
              ]}
              allLabel="All"
            />
            {canMark && (
              // Always here (disabled until something is ticked), so ticking never moves the rows.
              <span className="ml-auto flex items-end gap-2 self-end">
                <Button
                  size="sm"
                  leadingIcon={<CheckCircle />}
                  disabled={pickedRows.length === 0}
                  onClick={() =>
                    setConfirm({ ids: pickedRows.map((r) => r.cultivationId), ready: true })
                  }
                >
                  Mark {pickedRows.length || ''} ready
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  leadingIcon={<XCircle />}
                  disabled={pickedRows.length === 0}
                  onClick={() =>
                    setConfirm({ ids: pickedRows.map((r) => r.cultivationId), ready: false })
                  }
                >
                  Not ready
                </Button>
              </span>
            )}
          </>
        }
        empty={
          <EmptyState
            icon={<Plant />}
            title={f.groupId ? 'No cultivations' : 'Pick a village'}
            description={
              f.groupId
                ? "No crop is sown on this village's farms, or every one is already harvested."
                : 'Its farms and their cultivations show here, with the farmers on each farm.'
            }
          />
        }
      />
      <ConfirmDialog
        open={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        title={
          confirm?.ready
            ? `Mark ${confirm.ids.length} ready for harvest`
            : `Take the ready mark off ${confirm?.ids.length ?? 0}`
        }
        description={
          confirm?.ready
            ? 'The gate can then buy these crops from their farmers. Phones get it with their next roster download.'
            : 'The gate stops offering these crops; a farmer with no other ready crop cannot sell until one is marked.'
        }
        confirmLabel={confirm?.ready ? 'Mark ready' : 'Take the mark off'}
        tone={confirm?.ready ? undefined : 'danger'}
        loading={mark.isPending}
        onConfirm={() =>
          confirm
            ? mark.mutateAsync({ cultivationIds: confirm.ids, ready: confirm.ready }).then(() => {
                setConfirm(null)
                setPicked(new Set())
              })
            : undefined
        }
      />
    </ConsolePage>
  )
}
